import React, { useState, useEffect } from "react";
import moment from "moment";
import { connect } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { Select } from "expano-components";
import DatePicker from "react-date-picker";
import { Main } from "layouts";
import { client_v2, parseQuery } from "utils/api";

import { getPackerDashboard } from "actions/packer_dashboard";

import TransactionsTable from "./components/TransactionsTable";
import SummaryTable from "./components/SummaryTable";
import TransactionCharts from "./components/TransactionCharts";

import { ReactComponent as DashboardIcon } from "icons/dashboard.svg";
import { ReactComponent as CalendarIcon } from "icons/calendar.svg";
import PayoutTransactions from "./components/PayoutTransactions";
import UserAdjustmentModal from "./components/PayoutTransactions/components/UserAdjustmentModal";

let timer;

const tabs = [
  { id: "summary", name: "Szczegóły" },
  { id: "transactions", name: "Rozliczenia" },
];

const PackerDashboard = ({
  packer_dashboard: { data, status, packer },
  getPackerDashboard,
  current_user,
  location: { search },
}) => {
  const { packer_id, tab, date: initial_date } = parseQuery(search);
  const [is_open_user_adjustment_modal, setOpenUserAdjustmentModal] =
    useState(false);
  const is_admin = current_user.role === "admin";
  const today = moment();
  const [active_tab, setActiveTab] = useState(tab || "summary");
  const [date, setDate] = useState(initial_date ? moment(initial_date) : today);
  const [selected_date, setSelectedDate] = useState(null);
  const [user_id, setUserId] = useState(is_admin ? packer_id : current_user.id);

  const { data: packers } = useQuery(
    ["packers"],
    () => client_v2.get("/users/packers"),
    {
      enabled: is_admin,
      select: (res) => res.data,
      retry: false,
    }
  );

  const packers_options = [
    { label: "Wszyscy", value: null, user_ids: [] },
    ...(packers || []).map(({ id, first_name, last_name, user_ids }) => ({
      label: `${first_name} ${last_name}`,
      value: id,
      user_ids,
    })),
  ];

  useEffect(() => {
    if (date) {
      getPackerDashboard({ date: date.format(), user_id });
    }
    return clearTimeout(timer);
  }, [date, user_id]);

  const refetch = () => getPackerDashboard({ date: date.format(), user_id });

  const handleMarkRow = (date) => {
    setActiveTab("summary", () => {});
    setSelectedDate(date);
    setTimeout(() => {
      window.scrollTo({
        top: document.querySelector(`.table-row--active`).offsetTop,
        left: 0,
        behavior: "smooth",
      });
    }, 100);
    timer = setTimeout(() => {
      clearTimeout(timer);
      setSelectedDate(null);
    }, 3000);
  };

  const user_balances = data?.all_packer_stats?.map(
    ({ user, balance, can_generate_payout }) => ({
      user_ids: user?.user_ids,
      balance,
      can_generate_payout,
    })
  );

  return (
    <Main
      is_loading={["invalid", "failure"].includes(status)}
      page={{
        name: is_admin ? "Statystyki pakowaczy" : "Dashboard",
        icon: <DashboardIcon />,
      }}
    >
      {date && packer?.id && is_open_user_adjustment_modal && (
        <UserAdjustmentModal
          date={date.format()}
          user_id={packer?.id}
          full_name={packer?.full_name}
          refetch={refetch}
          handleClose={() => setOpenUserAdjustmentModal()}
        />
      )}
      <div className="dashboard m-b-0">
        <div className="dashboard__header row">
          <div className="box-4">
            {is_admin ? (
              <Select
                handleChange={(value) => setUserId(value)}
                value={packers_options?.find((opt) =>
                  opt.user_ids.includes(Number(user_id))
                )}
                options={packers_options || []}
                placeholder="Wybierz pakera..."
              />
            ) : (
              <h2 className="dashboard__title heading">
                Statystyki - {packer?.full_name}
              </h2>
            )}
          </div>
          <div className="box-4 text-center">
            <h2 className="dashboard__title heading">
              {moment(date).locale("pl-PL").format("MMMM YYYY")}
            </h2>
          </div>
          <div className="box-4">
            <div className="row jc-fe">
              {is_admin && date && packer?.full_name && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger m-r-10"
                  onClick={() => setOpenUserAdjustmentModal(true)}
                >
                  Dodaj uwagę
                </button>
              )}
              <div className="calendar-group">
                <DatePicker
                  calendarIcon={<CalendarIcon />}
                  onChange={(val) => setDate(moment(val))}
                  value={date.toDate()}
                  showLeadingZeros={false}
                  maxDetail="year"
                  clearIcon={null}
                  locale="pl-PL"
                  format="MMMM"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {status === "success" && (
        <>
          {is_admin && !packer && (
            <>
              <TransactionCharts data={data} setUserId={setUserId} />
              <PayoutTransactions
                data={data.payout_transactions}
                packers={packers}
                refetch={refetch}
                date={date.format()}
                user_balances={user_balances}
              />
            </>
          )}
          {packer && data && Object.keys(data).length > 0 && (
            <>
              <ul className="dashboard__tabs">
                {tabs.map(({ id, name }) => (
                  <li
                    key={id}
                    className={`tab dashboard-tab-text ${
                      id === active_tab ? "active" : ""
                    }`}
                    onClick={() => setActiveTab(id)}
                  >
                    <span>{name}</span>
                  </li>
                ))}
              </ul>
              {active_tab === "summary" && (
                <SummaryTable selected_date={selected_date} />
              )}
              {active_tab === "transactions" && (
                <TransactionsTable handleMarkRow={handleMarkRow} />
              )}
            </>
          )}
        </>
      )}
    </Main>
  );
};

export default connect(
  ({ packer_dashboard, current_user }) => ({ packer_dashboard, current_user }),
  (dispatch) => ({
    getPackerDashboard: (date) => dispatch(getPackerDashboard(date)),
  })
)(PackerDashboard);
